import { message as antdMessage } from "antd";
import axios from "axios";
import { saveAs } from "file-saver";
import i18n from "i18next";
import { omit } from "lodash";
import path from "path-browserify";

import { logout } from "~/app/utils";

import { BASE_URL } from "~/app/constant/env";

// i18n translate function with errorSuccessMessages namespace applied
const t = (text, data) => i18n.t(text, { ns: "errorSuccessMessages", ...data });

// Function to get the message returned with an error (if provided)
export const getMessageFromError = (err) => {
  const errorMessage =
    err.response?.data?.msg ||
    err.response?.data?.message ||
    err.response?.data?.err ||
    err.response?.data?.error;
  return errorMessage ? t(errorMessage, err.response.data) : undefined;
};

// Function to get the message returned with an response (if provided)
export const getMessageFromResponse = (res) => {
  const responseMessage = res.data?.msg || res.data?.message;
  return responseMessage ? t(responseMessage, res.data) : undefined;
};

// Function to display error message from response
export const handleError = (err, msg) => {
  const message = window.antdMessageApi || antdMessage;
  // get error status code
  const status = err.response?.status;
  if (typeof msg === "string") {
    message.error(t(msg), 5);
  } else if (typeof msg === "object" && msg[status]) {
    const messageToDisplay =
      typeof msg[status] === "object" ? msg[status] : t(msg[status]);
    message.error(messageToDisplay, 5);
  } else {
    message.error(
      getMessageFromError(err) || t("An error has occurred. Please try again."),
      5
    );
  }
  throw err;
};

// Function to display success message from response
export const handleResponse = (res, msg) => {
  const message = window.antdMessageApi || antdMessage;
  const status = res?.status;
  const responseMessage = getMessageFromResponse(res);
  if (typeof msg === "string") {
    message.success(t(msg));
  } else if (typeof msg === "object" && msg[status]) {
    message.success(t(msg[status]));
  } else if (responseMessage) {
    message.success(responseMessage);
  }
  return res;
};

// Function to download file from response
export const downloadResponseAsFile = (res) => {
  // Get filename from response headers (if available)
  const filename = res.headers["content-disposition"]
    ?.split(";")
    ?.find((n) => n.includes("filename="))
    ?.replace("filename=", "")
    ?.trim();

  // Prompt download
  saveAs(res.data, filename);
};

// Function to convert a data object to Form Data with a key for file uploads
export const convertObjectToFormData = (
  data,
  uploadsKey = "uploads",
  destinationKey = "files[]"
) => {
  // create FormData object that will be sent to endpoint
  let formData = new FormData();

  // append a "files[]" key containing Files objects from data values
  if (data[uploadsKey]) {
    // console.log("files array to append to formData :>>", data[uploadsKey]);
    data[uploadsKey].forEach((file) => {
      formData.append(destinationKey, file.originFileObj || file);
    });
  }

  // Stringify the data but ommit the files
  const body = JSON.stringify(omit(data, uploadsKey));

  // append the rest of the data (everything but uploadsKey) as a stringified json into a "body" key of the FormData
  formData.append("body", body);

  // return formData value
  return formData;
};

// Request wrapper
export const requestWrapper = axios.create({
  baseURL: path.join(BASE_URL, "/api"),
});

requestWrapper.interceptors.response.use(
  (response) => {
    //if no error you get the response
    return response;
  },
  (error) => {
    const { status } = error.response;
    const original_request = error.config;
    if (
      // If the request fails with 401 or 422
      (status === 401 || status === 422) &&
      // and is NOT a refresh or a logout request
      !["/refresh", "/logout"].some((x) => original_request.url.includes(x))
    ) {
      let refreshed = false;
      return requestWrapper
        .post("/a12n/refresh")
        .then(() => {
          // set refreshed to true to allow the original request to fail 401 again, even after a successful refresh.
          refreshed = true;
          return axios(original_request);
        })
        .catch((err) => {
          // If the user authentication can't be refreshed, the user is loged out.
          // Except if the refresh has been successful already.
          // This will allow the request to fail 401 again for another reason than an expired token
          if (!refreshed) {
            logout();
          } else {
            throw err;
          }
        });
    } else {
      throw error;
    }
  }
);

// Fake response function to work with stubs
const randomIntFromInterval = (min, max) =>
  Math.floor(Math.random() * (max - min + 1) + min);

export const fakeResponse = (data) => {
  return new Promise((resolve) => {
    setTimeout(
      () => {
        resolve(data);
      },
      randomIntFromInterval(500, 1000)
    );
  });
};
